import { PropsWithChildren } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, NavLink, Outlet } from 'react-router-dom'
import styled from 'styled-components'
import { strings } from './AppLayout.strings'
import { Routes } from 'routes'
import menuIcon from '@/assets/menu.svg'
import pgb from '@/assets/images/logo.svg'
import { useAuthenticationState, useLayoutInfo } from '@/ui/core/hooks'
import { Popover } from '@/ui/components'
import { appConfig } from '@/ui/core/config'

const Layout = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-rows: auto 1fr auto;
  grid-template-areas:
    'nav nav nav'
    '. main main'
    'footer footer footer';
  row-gap: 2em;
  height: 100vh;
`

const Main = styled.main`
  grid-area: main;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-self: center;
  padding: 0 0.5em;
  max-width: 1400px;
  width: 100%;
  padding-bottom: 2rem;
`

const NavContainer = styled.div<{ $large: boolean }>`
  display: flex;
  justify-content: center;
  grid-area: nav;
  padding-top: 8px;
  padding-bottom: 8px;

  > nav {
    padding: ${({ $large }) => ($large ? 0 : '12px')};
    max-width: 1400px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: ${({ $large }) =>
      $large ? 'space-around' : 'space-between'};

    > a {
      justify-self: start;
      text-transform: uppercase;
      &:hover {
        color: ${({ theme }) => theme.colors.brand.primary.hover};
      }
    }
  }
`

const StyledNavLink = styled(NavLink)`
  text-decoration: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: var(--xl-text);
  color: ${({ theme }) => theme.colors.font.regular};
  font-weight: 300;
  text-transform: uppercase;
  padding: 4px;
  &:hover {
    color: ${({ theme }) => theme.colors.brand.primary.hover};
    border-radius: 4px;
  }
`

const Logo = styled.img`
  height: 34;
  width: 100px;
`

const Kebab = styled.img`
  height: 24px;
`

const Footer = styled.footer`
  grid-area: footer;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 3em;
`

const TermContainer = styled.div`
  display: flex;
  gap: 10px;
`

const protectedLinks = [
  <StyledNavLink key={'dashboard'} to={Routes.dashboard}>
    <FormattedMessage {...strings.DashboardLink} />
  </StyledNavLink>,
  <StyledNavLink key={'settings'} to={Routes.settings}>
    <FormattedMessage {...strings.settingsLink} />
  </StyledNavLink>,
]

const publicLinks = [
  <StyledNavLink key={'contact'} to={Routes.contact}>
    <FormattedMessage {...strings.contactLink} />
  </StyledNavLink>,
].concat(
  appConfig.enablePricing
    ? [
        <StyledNavLink key={'pricing'} to={Routes.pricing}>
          <FormattedMessage {...strings.pricingLink} />
        </StyledNavLink>,
      ]
    : [],
)

const authLinks = [
  <StyledNavLink key={'authentication'} to={Routes.authentication}>
    <FormattedMessage {...strings.signInLink} />
  </StyledNavLink>,
]

interface Props extends PropsWithChildren {
  className?: string
  showFooter?: boolean
}

// TODO: Think about nav in the context of new browser router
export const AppLayout: React.FC<Props> = ({ className, showFooter }) => {
  const { size } = useLayoutInfo()
  const { isSignedIn } = useAuthenticationState()
  const intl = useIntl()

  const large = size === 'large'

  const navItems = isSignedIn ? protectedLinks : authLinks

  return (
    <Layout>
      <NavContainer $large={large}>
        <nav>
          <div>
            <NavLink to={Routes.landing}>
              <Logo src={pgb} alt={intl.formatMessage(strings.logoAlt)} />
            </NavLink>
          </div>
          {large ? (
            <>
              {publicLinks.map((link) => link)}
              {navItems.map((items) => items)}
            </>
          ) : (
            <Popover
              label={
                <Kebab
                  src={menuIcon}
                  alt={intl.formatMessage(strings.menuAlt)}
                />
              }
              elements={publicLinks.concat(navItems)}
            />
          )}
        </nav>
      </NavContainer>
      <Main className={className}>
        <Outlet />
      </Main>
      {showFooter ? (
        <Footer>
          <span>
            <FormattedMessage
              {...strings.copyright}
              values={{ date: new Date().getFullYear() }}
            />
          </span>
          <TermContainer>
            <Link to={Routes.terms}>
              <FormattedMessage {...strings.termsOfUse} />
            </Link>
            <>&#9679;</>
            <Link to={Routes.privacy}>
              <FormattedMessage {...strings.privacyPolicy} />
            </Link>
          </TermContainer>
        </Footer>
      ) : null}
    </Layout>
  )
}
